'use client'
import { notFound } from 'next/navigation'
import { type ReactNode, useState } from 'react'
import { ApiRouteNames, useApi } from '@/api'
import DonationAlert from '@/streambot/DonationAlert'
import LastComment from '@/streambot/LastComment'
import LastDonation from '@/streambot/LastDonation'
import Milestones from '@/streambot/Milestones'
import Progress from '@/streambot/Progress'
import ProjectLogos from '@/streambot/ProjectLogos'
import Standard from '@/streambot/Standard'
import TopDonation from '@/streambot/TopDonation'
import TopDonor from '@/streambot/TopDonor'
import Total from '@/streambot/Total'
import Voting from '@/streambot/Voting'
import { Widgets } from '@/streambot/Widgets'
import PreviewModeWarning from '@/streambotPage/_dependencies/PreviewModeWarning'

const WidgetComponentMap = {
  [Widgets.LastComment]: LastComment,
  [Widgets.Standard]: Standard,
  [Widgets.Progress]: Progress,
  [Widgets.LastDonation]: LastDonation,
  [Widgets.TopDonation]: TopDonation,
  [Widgets.TopDonor]: TopDonor,
  [Widgets.ProjectLogos]: ProjectLogos,
  [Widgets.Voting]: Voting,
  [Widgets.Total]: Total,
  [Widgets.DonationAlert]: DonationAlert,
  [Widgets.Milestones]: Milestones,
} as const

type StreambotPageContentProps<TWidget extends Widgets> = {
  widget: TWidget
  fundraisingEventId: string
  preview: boolean | undefined
}

function is404(error: Error | null | undefined) {
  return (
    error &&
    error.cause !== null &&
    typeof error.cause === 'object' &&
    'status' in error.cause &&
    error.cause.status! === 404
  )
}

function useGetWidgetSettings(fundraisingEventId: string) {
  const { data, error } = useApi(
    ApiRouteNames.apiWidgetSettings,
    { slug: fundraisingEventId },
    { pathNameOnly: true, intervalMs: 5000 }
  )
  if (is404(error)) {
    notFound()
  }
  return data?.widgetSettings
}

export function StreambotWidgetWrapper({
  fundraisingEventId,
  preview,
  children,
}: Omit<StreambotPageContentProps<Widgets>, 'widget'> & { children?: ReactNode }) {
  return (
    <>
      <PreviewModeWarning show={preview} fundraisingEventId={fundraisingEventId} />
      {children}
    </>
  )
}

export function useGetWidgetComponentProps<TWidget extends Widgets>(
  widget: TWidget,
  fundraisingEventId: string,
  preview: boolean | undefined
) {
  const widgetSettings = useGetWidgetSettings(fundraisingEventId)
  const showPreview = preview ?? widgetSettings?.global?.previewMode
  const [props, setProps] = useState(widgetSettings?.[widget])
  if (widgetSettings?.[widget] && props !== widgetSettings?.[widget]) {
    setProps(widgetSettings?.[widget])
  }

  return { props, showPreview }
}

function StreambotPageContent<TWidget extends Widgets>({
  widget,
  fundraisingEventId,
  preview,
}: StreambotPageContentProps<TWidget>) {
  const { props, showPreview } = useGetWidgetComponentProps(widget, fundraisingEventId, preview)
  const WidgetComponent = WidgetComponentMap[widget]
  if (!WidgetComponent) notFound()

  return (
    <StreambotWidgetWrapper fundraisingEventId={fundraisingEventId} preview={showPreview}>
      <WidgetComponent
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        {...(props as any)}
        withDemoData={showPreview}
        fundraisingEventId={fundraisingEventId}
      />
    </StreambotWidgetWrapper>
  )
}

export default StreambotPageContent
